/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import PropTypes from 'prop-types' // Npm: react.js library.
import JoiBrowser from 'joi-browser' // Npm: Joi for frontend validation.
import _ from 'underscore' // Npm: Underscore.js library.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { toast } from 'react-hot-toast' // Npm: React hot toast.
import { useLazyQuery, useMutation } from '@apollo/client' // Npm: Apollo client.


/*
 * PACKAGES
 */
import SubmitButton from 'components/SubmitButton'
import { MemoizedInput } from 'components/MemoizedInput'


/*
 * GRAPHS
 */
import SenderIdReadUniqueQuery from './__query__/index.senderId.read.query'
import SenderIdUpsertMutation from './__mutation__/index.senderId.upsert.mutation'


/*
 * OBJECTS
 */
const Index = ({ isOpen, isCreateOnly, onClose, passOn }) => {
  // Hook assignment.
  const [error, setError] = React.useState('')
  const [forceReRender, setForceReRender] = React.useState('')
  const [QuerySenderIdReadUnique, QuerySenderIdReadUniqueResponse] = useLazyQuery(SenderIdReadUniqueQuery, { 'variables': { 'senderIdId': passOn?.senderIdId } })
  const [MutationSenderIdUpsert, MutationSenderIdUpsertResponse] = useMutation(SenderIdUpsertMutation)
  const _formDataRef = React.useRef({})

  // Object assignment.
  const _SubmitForm = async e => {
    // Prevent default behavior.
    e.preventDefault()

    // Reset error.
    setError('')

    // Const assignment.
    const _JoiSchema = JoiBrowser.object({
      'senderId': JoiBrowser.string().required()
    }).options({ 'allowUnknown': true })

    // Remove all keys from _formDataRef.current which are undefined.
    _formDataRef.current = _.pick(_formDataRef.current, _.identity)

    // Validate form data.
    const _JoiSchemaValidate = _JoiSchema.validate(_formDataRef.current)

    // If error exists then report failure.
    if (_JoiSchemaValidate.error) return setError(_JoiSchemaValidate.error?.message)

    // Execute account registration mutation.
    const _MutationSenderIdUpsert = await MutationSenderIdUpsert({
      'variables': {
        'senderIdDirectoryId': isCreateOnly ? passOn?.senderIdDirectoryId : void 0,
        'senderId': _formDataRef?.current?.senderId,
        'senderIdId': isCreateOnly ? void 0 : passOn?.senderIdId
      }
    })

    // If mutation caught an exception then report failure.
    if (_MutationSenderIdUpsert instanceof Error) return _MutationSenderIdUpsert

    // On Successful response from the mutation.
    if ('UPSERT_SUCCESSFUL' === _MutationSenderIdUpsert?.data?.SenderIdUpsert?.status) onClose?.()

    // Style Guide.
    return toast(_MutationSenderIdUpsert?.data?.SenderIdUpsert?.message)
  }

  // Event handler.
  React.useEffect(() => {
    // _Async handler.
    const _Async = async () => {
      // Const assignment.
      const _QuerySenderIdReadUniqueQuery = await QuerySenderIdReadUnique({ 'variables': { 'senderIdId': isCreateOnly ? 'UN_KNOWN' : passOn?.senderIdId } })

      // If query caught an exception then report failure.
      if (_QuerySenderIdReadUniqueQuery instanceof Error) return _QuerySenderIdReadUniqueQuery

      /*
       * If customer details fetch complete then
       * update its value.
       */
      if (_QuerySenderIdReadUniqueQuery?.data?.SenderIdReadUnique) {
        // Update form data.
        _formDataRef.current = {
          'senderIdId': passOn?.senderIdId,
          'senderId': _QuerySenderIdReadUniqueQuery?.data?.SenderIdReadUnique?.senderId
        }

        // Update state.
        return setForceReRender(String.random(8))
      }

      // Report failure.
      return void 0
    }; _Async()
  }, [passOn, isOpen])

  // Const assignment.
  const _isLoading = MutationSenderIdUpsertResponse.loading
  const _isInputDisabled = isCreateOnly ? false : _isLoading || QuerySenderIdReadUniqueResponse?.loading

  // Return component.
  return (
    <form style={{ 'width': '100%' }} onSubmit={_SubmitForm} key={forceReRender}>
      <MemoizedInput
        disabled={_isInputDisabled}
        isRequired={true}
        name='senderId'
        label='Sender Id'
        placeholder='e.g. "GOOGLE"'
        onChange={({ target }) => {
          // Over spreading.
          const { name, value } = target

          // Update form data.
          _formDataRef.current = {
            ..._formDataRef?.current,
            [name]: value
          }
        }}
        error={error}
        isInvalid={error?.includes('senderId')}
        data={_formDataRef?.current?.senderId}
      />
      <SubmitButton
        onSubmit={_SubmitForm}
        disabled={_isInputDisabled}
        isLoading={_isLoading}
        defaultText={isCreateOnly ? 'Create SenderId' : 'Update SenderId'} />
    </form>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'isCreateOnly': PropTypes.bool,
  'isOpen': PropTypes.bool,
  'onClose': PropTypes.func,
  'passOn': PropTypes.object
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index)
